import { identity } from "@suse-o11y/identity";
import { isFunction } from "@suse-o11y/is-function";
import { isPresent } from "@suse-o11y/is-present";
import type { MakeKeysOptional } from "@suse-o11y/make-keys-optional";
import { mapChildren } from "@suse-o11y/map-children";
import { times } from "@suse-o11y/times";
import { usePseudoRandomIntGenerator } from "@suse-o11y/use-pseudo-random-int-generator";
import React from "react";

import type { PolymorphicBoxProps } from "~/components/polymorphic-box";

import styles from "./_skeleton.module.css";
import { SkeletonBoundary } from "./boundary";

const DEFAULT_ELEMENT = React.Fragment;

type SkeletonTextRenderer<TValue> = (value: TValue) => React.ReactNode;

export interface SkeletonTextProps<TValue> {
	className?: string;
	styles?: React.CSSProperties;

	when?: undefined | null | TValue;

	/**
	 * @default 1
	 */
	lines?: number;

	/**
	 * When true, the width of the skeleton text lines is randomized.
	 *
	 * @default false
	 */
	randomize?: boolean;

	children: SkeletonTextRenderer<TValue> | Array<React.ReactNode | SkeletonTextRenderer<TValue>>;
}

interface SkeletonTextComponent {
	<TValue extends string | number, TElement extends React.ElementType = typeof DEFAULT_ELEMENT>(
		props: PolymorphicBoxProps<TElement, MakeKeysOptional<SkeletonTextProps<TValue>, "children">>,
	): React.ReactNode;

	<TValue, TElement extends React.ElementType = typeof DEFAULT_ELEMENT>(
		props: PolymorphicBoxProps<TElement, SkeletonTextProps<TValue>>,
	): React.ReactNode;
}

export const SkeletonText: SkeletonTextComponent = React.forwardRef(
	(
		{
			el,
			when,
			lines = 1,
			children = identity,
			randomize = false,
			...props
		}: PolymorphicBoxProps<React.ElementType, SkeletonTextProps<string>>,
		ref,
	) => {
		const generatePseudoRandomWidth = usePseudoRandomIntGenerator({
			min: 30,
			max: 100,
			step: 5,
		});

		const element = el ?? (props.className || props.styles ? "div" : DEFAULT_ELEMENT);

		return (
			<SkeletonBoundary el={element} ref={ref} when={when} {...props}>
				{mapChildren(children, (child, index) => {
					if (isFunction(child)) {
						if (isPresent(when)) {
							return <React.Fragment key={index}>{child(when)}</React.Fragment>;
						}

						return times(lines, (lineIndex) => (
							<SkeletonTextLine key={lineIndex} width={randomize ? generatePseudoRandomWidth(lineIndex) : 100} />
						));
					}

					if (!React.isValidElement(child)) {
						return child;
					}

					return <React.Fragment key={index}>{child}</React.Fragment>;
				})}
			</SkeletonBoundary>
		);
	},
);

const SkeletonTextLine: React.FC<{
	width: number;
}> = ({ width }) => (
	<span className={styles.skeletonTextLine} data-testid="skeleton-text-line" style={{ width: `${width}%` }}>
		&zwnj;
	</span>
);
